import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  FormSection,
  PageShell,
  RadixSelect,
  SectionCard,
  SegmentedControl,
  StickyFooter,
  Switch,
  toast,
  type SelectOption,
} from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  TEMPLATE_LANGUAGES,
  type KakaoTemplateType,
  type TemplateApprovalStatus,
  type TemplateCreatePayload,
  type TemplateLanguageCode,
  type TemplateUpdatePayload,
} from '../types/notificationTemplate';
import type { NotificationChannel } from '../types/notificationMessage';
import {
  useCreateNotificationTemplateMutation,
  useDeleteNotificationTemplateMutation,
  useNotificationTemplateQuery,
  useUpdateNotificationTemplateMutation,
  TemplateApiError,
} from '../hooks/useNotificationTemplatesQuery';
import { NOTIFICATION_TEMPLATE_WRITE_ROLES } from '../lib/notificationActionRbac';
import { languageLabel } from '../lib/notificationLabels';
import {
  EMPHASIS_SUBTITLE_MAX_LENGTH,
  EMPHASIS_TITLE_MAX_LENGTH,
  KAKAO_BUTTON_TYPES,
  createEmptyButton,
  extractVariableKeys,
  fromTemplateButtons,
  normalizeKakaoTemplateType,
  parseVariablesJson,
  sanitizeButtons,
  toVariablesJson,
  validateKakaoTemplate,
} from '../lib/kakaoTemplate';
import type { KakaoButtonType, TemplateButton } from '../types/notificationTemplate';
import type { TemplateVariable } from '../lib/kakaoTemplate';
import { StepWizard, type WizardStep } from '../components/StepWizard';
import { KakaoBubblePreview, SmsBubblePreview } from '../components/KakaoBubblePreview';
import { EmailPreview } from '../components/EmailPreview';
import { extractPreviewKeys } from '../lib/emailHtmlPreview';

const TEMPLATE_CODE_PATTERN = /^[A-Z_][A-Z0-9_]*$/;

/**
 * 변수 패널에 표시할 토큰 문자열 — godomall `rc_` 키는 단일중괄호 `{rc_xxx}`, 그 외는 `#{key}`.
 * (본문에서 쓰인 실제 토큰 형태와 일치시켜 운영자 혼동 방지.)
 */
const tokenDisplay = (key: string): string => (key.startsWith('rc_') ? `{${key}}` : `#{${key}}`);

const denseInputCls =
  'h-9 w-full rounded-md px-2.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]';
const denseInputStyle = {
  background: 'var(--surface-0)',
  border: '1px solid var(--divider-strong)',
  color: 'var(--content-strong)',
} as const;

interface FormState {
  templateCode: string;
  channel: NotificationChannel;
  languageCode: TemplateLanguageCode;
  subject: string;
  body: string;
  kakaoTemplateType: KakaoTemplateType;
  emphasisTitle: string;
  emphasisSubtitle: string;
  imageUrl: string;
  reprLinkMobile: string;
  reprLinkPc: string;
  providerTemplateCode: string;
  approvalStatus: TemplateApprovalStatus;
  autoSendEnabled: boolean;
  isAdvertising: boolean;
  /** ID-226 — EMAIL HTML 본문 발송 토글(default false=기존 plain). EMAIL 채널에서만 노출/전송. */
  isHtml: boolean;
}

const INITIAL: FormState = {
  templateCode: '',
  channel: 'KAKAO_BIZ',
  languageCode: 'KO_KR',
  subject: '',
  body: '',
  kakaoTemplateType: 'basic',
  emphasisTitle: '',
  emphasisSubtitle: '',
  imageUrl: '',
  reprLinkMobile: '',
  reprLinkPc: '',
  providerTemplateCode: '',
  approvalStatus: 'draft',
  autoSendEnabled: false,
  isAdvertising: false,
  isHtml: false,
};

export const NotificationTemplateFormPage = () => {
  const { t } = useTranslation('notifications');
  const navigate = useNavigate();
  const { templateId } = useParams<{ templateId: string }>();
  const isEdit = Boolean(templateId) && templateId !== 'new';
  const id = templateId ?? '';
  const canWrite = useHasRole(NOTIFICATION_TEMPLATE_WRITE_ROLES);

  const detailQuery = useNotificationTemplateQuery(isEdit ? id : undefined);
  const createMutation = useCreateNotificationTemplateMutation();
  const updateMutation = useUpdateNotificationTemplateMutation();
  const deleteMutation = useDeleteNotificationTemplateMutation();

  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormState>(INITIAL);
  const [variables, setVariables] = useState<TemplateVariable[]>([]);
  const [buttons, setButtons] = useState<TemplateButton[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);

  const isKakao = form.channel === 'KAKAO_BIZ';
  const isEmail = form.channel === 'EMAIL';

  // 편집 hydration.
  useEffect(() => {
    if (!isEdit || !detailQuery.data) return;
    const d = detailQuery.data;
    setForm({
      templateCode: d.templateCode,
      channel: d.channel,
      languageCode: d.languageCode,
      subject: d.subject ?? '',
      body: d.body,
      kakaoTemplateType: normalizeKakaoTemplateType(d.kakaoTemplateType),
      emphasisTitle: d.emphasisTitle ?? '',
      emphasisSubtitle: d.emphasisSubtitle ?? '',
      // 이미지형 대표 이미지 URL(BE imageUrl). 편집 시 기존 이미지 복원.
      imageUrl: d.imageUrl ?? '',
      reprLinkMobile: d.reprLinkMobile ?? '',
      reprLinkPc: d.reprLinkPc ?? '',
      providerTemplateCode: d.providerTemplateCode ?? '',
      approvalStatus: d.approvalStatus ?? 'draft',
      autoSendEnabled: d.autoSendEnabled,
      isAdvertising: d.isAdvertising,
      isHtml: d.isHtml,
    });
    setVariables(parseVariablesJson(d.variablesJson));
    setButtons(fromTemplateButtons(d.buttons));
  }, [detailQuery.data, isEdit]);

  // 본문 토큰 ↔ 변수 목록 동기화(기존 example 보존).
  // EMAIL HTML 본문은 godomall `{rc_xxx}` 단일중괄호 토큰도 추출(extractPreviewKeys),
  // 그 외(알림톡/SMS/plain)는 기존 `#{var}` 정본 추출(extractVariableKeys) — 회귀0.
  useEffect(() => {
    const keys =
      isEmail && form.isHtml ? extractPreviewKeys(form.body) : extractVariableKeys(form.body);
    setVariables((prev) => {
      const exampleByKey = new Map(prev.map((v) => [v.key, v.example]));
      return keys.map((key) => ({
        key,
        example: exampleByKey.get(key) ?? '',
      }));
    });
  }, [form.body, isEmail, form.isHtml]);

  const set = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const channelOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: 'KAKAO_BIZ', label: t('channel.KAKAO_BIZ', '알림톡') },
      { value: 'SMS', label: t('channel.SMS', 'SMS') },
      { value: 'EMAIL', label: t('channel.EMAIL', '이메일') },
    ],
    [t],
  );
  const languageOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => TEMPLATE_LANGUAGES.map((l) => ({ value: l, label: languageLabel(t, l) })),
    [t],
  );
  const approvalOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => [
      { value: 'draft', label: t('template.approval.draft', '작성 중') },
      { value: 'pending', label: t('template.approval.pending', '검수 대기') },
      { value: 'approved', label: t('template.approval.approved', '승인 완료') },
      { value: 'rejected', label: t('template.approval.rejected', '반려') },
    ],
    [t],
  );
  const buttonTypeOptions = useMemo<ReadonlyArray<SelectOption>>(
    () => KAKAO_BUTTON_TYPES.map((bt) => ({ value: bt, label: t(`template.buttons.type_${bt}`) })),
    [t],
  );

  const steps = useMemo<WizardStep[]>(
    () => [
      { key: 'basic', label: t('template.step.basic', '기본 정보') },
      { key: 'content', label: t('template.step.content', '내용 설정') },
      { key: 'design', label: t('template.step.design', '디자인 설정') },
      { key: 'preview', label: t('template.step.preview', '미리보기') },
    ],
    [t],
  );

  const breadcrumb = useMemo(
    () => [
      { label: t('page.group', '알림') },
      { label: t('page.breadcrumbMessage', '메시지(알림톡)') },
      { label: t('template.title', '템플릿 관리'), href: '/notifications?tab=template' },
      {
        label: isEdit
          ? t('template.editTitle', '템플릿 수정')
          : t('template.createTitle', '템플릿 등록'),
      },
    ],
    [isEdit, t],
  );

  const validateAll = useCallback((): string[] => {
    const errs: string[] = [];
    if (!form.templateCode.trim()) {
      errs.push(t('template.validate.templateCodeRequired', '템플릿 코드는 필수입니다.'));
    } else if (!TEMPLATE_CODE_PATTERN.test(form.templateCode)) {
      errs.push(
        t('template.validate.templateCodePattern', '대문자/숫자/언더스코어만 사용 가능합니다 (예: WELCOME_EMAIL)'),
      );
    }
    if (!form.body.trim()) errs.push(t('template.validate.bodyRequired', '본문은 필수입니다.'));
    if (isKakao) {
      const k = validateKakaoTemplate(t, variables, buttons, {
        kakaoTemplateType: form.kakaoTemplateType,
        emphasisTitle: form.emphasisTitle,
        emphasisSubtitle: form.emphasisSubtitle,
        imageUrl: form.imageUrl,
      });
      errs.push(...k.errors);
    }
    return errs;
  }, [form, isKakao, variables, buttons, t]);

  const isBusy =
    createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  const onSubmit = async () => {
    if (!canWrite) return;
    const errs = validateAll();
    setErrors(errs);
    if (errs.length > 0) {
      toast.error(errs[0] ?? t('template.toast.saveFail', '템플릿 저장에 실패했습니다.'));
      return;
    }
    const variablesJson = toVariablesJson(variables);
    const sanitizedButtons = isKakao ? sanitizeButtons(buttons) : [];
    // 이미지형일 때만 imageUrl 전송(그 외 null). 빈 값은 null.
    const imageUrlPayload =
      isKakao && form.kakaoTemplateType === 'image' && form.imageUrl.trim()
        ? form.imageUrl.trim()
        : null;
    const subjectTrimmed = form.subject.trim();
    try {
      if (isEdit) {
        const payload: TemplateUpdatePayload = {
          subject: subjectTrimmed.length > 0 ? subjectTrimmed : null,
          body: form.body,
          variablesJson,
          isAdvertising: form.isAdvertising,
          kakaoTemplateType: isKakao ? form.kakaoTemplateType : null,
          emphasisTitle: isKakao && form.kakaoTemplateType === 'emphasis' ? form.emphasisTitle : null,
          emphasisSubtitle: isKakao && form.kakaoTemplateType === 'emphasis' ? form.emphasisSubtitle : null,
          imageUrl: imageUrlPayload,
          buttons: sanitizedButtons,
          reprLinkMobile: isKakao && form.reprLinkMobile.trim() ? form.reprLinkMobile.trim() : null,
          reprLinkPc: isKakao && form.reprLinkPc.trim() ? form.reprLinkPc.trim() : null,
          approvalStatus: form.approvalStatus,
          providerTemplateCode: form.providerTemplateCode.trim() ? form.providerTemplateCode.trim() : null,
          autoSendEnabled: form.autoSendEnabled,
          // ID-226 — EMAIL 만 HTML 본문 발송 의미. 그 외 채널은 false(BE 무시).
          isHtml: isEmail ? form.isHtml : false,
        };
        const updated = await updateMutation.mutateAsync({ templateId: id, body: payload });
        toast.success(t('template.toast.updateSuccess', '템플릿이 수정되었습니다.'));
        navigate(`/notifications/templates/${updated.templateId}`);
        return;
      }
      const payload: TemplateCreatePayload = {
        templateCode: form.templateCode,
        channel: form.channel,
        languageCode: form.languageCode,
        subject: subjectTrimmed.length > 0 ? subjectTrimmed : null,
        body: form.body,
        variablesJson,
        kakaoTemplateType: isKakao ? form.kakaoTemplateType : null,
        emphasisTitle: isKakao && form.kakaoTemplateType === 'emphasis' ? form.emphasisTitle : null,
        emphasisSubtitle: isKakao && form.kakaoTemplateType === 'emphasis' ? form.emphasisSubtitle : null,
        imageUrl: imageUrlPayload,
        buttons: sanitizedButtons,
        reprLinkMobile: isKakao && form.reprLinkMobile.trim() ? form.reprLinkMobile.trim() : null,
        reprLinkPc: isKakao && form.reprLinkPc.trim() ? form.reprLinkPc.trim() : null,
        providerTemplateCode: form.providerTemplateCode.trim() ? form.providerTemplateCode.trim() : null,
        autoSendEnabled: form.autoSendEnabled,
        // ID-226 — EMAIL 만 HTML 본문 발송 의미. 그 외 채널은 false(BE 무시).
        isHtml: isEmail ? form.isHtml : false,
      };
      const created = await createMutation.mutateAsync(payload);
      toast.success(t('template.toast.createSuccess', '템플릿이 등록되었습니다.'));
      navigate(`/notifications/templates/${created.templateId}`);
    } catch (err) {
      if (err instanceof TemplateApiError && err.isConflict()) {
        toast.error(t('template.toast.conflict', '중복된 템플릿 키 조합입니다.'));
        return;
      }
      toast.error(friendlyMessage(err, t('template.toast.saveFail', '템플릿 저장에 실패했습니다.')));
    }
  };

  const handleDelete = () => {
    if (!isEdit) return;
    deleteMutation.mutate(id, {
      onSuccess: () => {
        toast.success(t('template.toast.deleteSuccess', '템플릿이 비활성화되었습니다.'));
        setConfirmDelete(false);
        navigate('/notifications?tab=template');
      },
      onError: (err) => {
        toast.error(err.message ?? t('template.toast.deleteFail', '템플릿 삭제에 실패했습니다.'));
      },
    });
  };

  const addButton = () => setButtons((prev) => [...prev, createEmptyButton()]);
  const removeButton = (idx: number) =>
    setButtons((prev) => prev.filter((_, i) => i !== idx));
  const updateButton = (idx: number, patch: Partial<TemplateButton>) =>
    setButtons((prev) => prev.map((b, i) => (i === idx ? { ...b, ...patch } : b)));

  if (isEdit && detailQuery.isError) {
    return (
      <PageShell
        title={t('template.editTitle', '템플릿 수정')}
        breadcrumb={breadcrumb}
        data-testid="notification-template-form-page"
      >
        <EmptyState
          title={
            (detailQuery.error as TemplateApiError | undefined)?.isNotFound()
              ? t('template.error.notFound', '템플릿을 찾을 수 없습니다.')
              : t('template.error.detailLoad', '템플릿을 불러오지 못했습니다.')
          }
          action={
            <Button variant="outline" size="sm" onClick={() => navigate('/notifications?tab=template')}>
              {t('template.actions.backToList', '목록으로')}
            </Button>
          }
        />
      </PageShell>
    );
  }

  const headerActions = (
    <Button variant="outline" size="sm" onClick={() => navigate('/notifications?tab=template')}>
      {t('template.actions.backToList', '목록으로')}
    </Button>
  );

  // 채널별 실시간 미리보기 — 알림톡 버블 / 이메일(HTML·plain) / SMS 버블. 두 단계(디자인·발송)에서 재사용.
  const previewPanel = isKakao ? (
    <KakaoBubblePreview
      type={form.kakaoTemplateType}
      channelName="Everline"
      body={form.body}
      emphasisTitle={form.emphasisTitle}
      emphasisSubtitle={form.emphasisSubtitle}
      imageUrl={form.imageUrl}
      buttons={buttons}
      variables={variables}
    />
  ) : isEmail ? (
    <EmailPreview
      subject={form.subject}
      body={form.body}
      variables={variables}
      isHtml={form.isHtml}
      subjectLabel={t('template.field.subject', '제목')}
      bodyLabel={t('template.field.body', '본문')}
      emptyLabel={t('template.preview.emptyBody', '본문을 입력하면 미리보기가 표시됩니다.')}
    />
  ) : (
    <SmsBubblePreview body={form.body} variables={variables} />
  );
  // 이메일 HTML 미리보기는 더 넓은 패널이 가독성에 유리.
  const previewColCls = isEmail ? 'w-full lg:w-[440px] lg:shrink-0' : 'w-full lg:w-[340px] lg:shrink-0';

  return (
    <PageShell
      title={
        isEdit
          ? t('template.editTitle', '템플릿 수정')
          : t('template.createTitle', '템플릿 등록')
      }
      breadcrumb={breadcrumb}
      actions={headerActions}
      data-testid="notification-template-form-page"
    >
      <div className="flex flex-col gap-4">
        <StepWizard steps={steps} current={step} onStepClick={(i) => setStep(i)} />

        {/* 단계 1 — 기본 정보 */}
        {step === 0 ? (
          <SectionCard
            title={t('template.section.basic', '기본 정보')}
            description={t('template.step.basic', '기본 정보')}
          >
            <div className="flex flex-col gap-5">
              <FormSection
                density="compact"
                required
                label={t('template.field.templateCode', '템플릿 코드')}
                hint={
                  isEdit
                    ? t('template.hint.templateCodeReadOnly', '내부 식별 코드입니다. 생성 후에는 변경할 수 없습니다.')
                    : t('template.hint.templateCodePattern', '예: WELCOME_EMAIL — 대문자/숫자/언더스코어만')
                }
              >
                <input
                  type="text"
                  readOnly={isEdit}
                  maxLength={50}
                  value={form.templateCode}
                  onChange={(e) => set('templateCode', e.target.value)}
                  placeholder="WELCOME_EMAIL"
                  className={`${denseInputCls} font-mono`}
                  style={{ ...denseInputStyle, background: isEdit ? 'var(--surface-1)' : 'var(--surface-0)' }}
                  aria-label={t('template.field.templateCode', '템플릿 코드')}
                />
              </FormSection>

              <FormSection density="compact" required label={t('template.field.channel', '채널')}>
                <SegmentedControl<NotificationChannel>
                  value={form.channel}
                  onChange={(v) => set('channel', v)}
                  options={channelOptions.map((o) => ({ value: o.value as NotificationChannel, label: o.label }))}
                  disabled={isEdit}
                  aria-label={t('template.field.channel', '채널')}
                />
              </FormSection>

              <FormSection density="compact" required label={t('template.field.language', '언어')}>
                <RadixSelect
                  value={form.languageCode}
                  onChange={(v) => set('languageCode', v as TemplateLanguageCode)}
                  options={languageOptions}
                  disabled={isEdit}
                  aria-label={t('template.field.language', '언어')}
                />
              </FormSection>

              {/* QA-435 — 검수 상태는 모든 채널에서 바꿀 수 있다(종전엔 알림톡만 보여 이메일·SMS 템플릿이 '작성 중'에 묶였다). */}
              <FormSection
                density="compact"
                label={t('template.field.approvalStatus', '검수 상태')}
                hint={
                  isKakao
                    ? t('template.hint.approvalManual', '카카오 검수 상태를 수동으로 설정하거나 비즈톡 조회로 동기화합니다.')
                    : t('template.hint.approvalNonKakao', '이메일·문자는 카카오 검수가 없습니다. 운영 확인을 마친 템플릿은 승인 완료로 두세요.')
                }
              >
                <RadixSelect
                  value={form.approvalStatus}
                  onChange={(v) => set('approvalStatus', v as TemplateApprovalStatus)}
                  options={approvalOptions}
                  aria-label={t('template.field.approvalStatus', '검수 상태')}
                />
              </FormSection>
              {isKakao ? (
                <>
                  <FormSection
                    density="compact"
                    label={t('template.field.providerTemplateCode', '카카오 템플릿 코드')}
                    hint={t('template.hint.providerTemplateCode', '실제 발송에 사용되는 비즈톡(카카오) 템플릿 코드입니다. 자유롭게 변경할 수 있으며, 발송 시 본문과 일치해야 합니다.')}
                  >
                    <input
                      type="text"
                      value={form.providerTemplateCode}
                      onChange={(e) => set('providerTemplateCode', e.target.value)}
                      className={`${denseInputCls} font-mono`}
                      style={denseInputStyle}
                      aria-label={t('template.field.providerTemplateCode', '카카오 템플릿 코드')}
                    />
                  </FormSection>
                </>
              ) : null}
            </div>
          </SectionCard>
        ) : null}

        {/* 단계 2 — 내용 작성 */}
        {step === 1 ? (
          <SectionCard
            title={t('template.section.content', '내용 설정')}
            description={t('template.step.content', '내용 설정')}
          >
            <div className="flex flex-col gap-5">
              {isEmail ? (
                <>
                  <FormSection density="compact" label={t('template.field.subject', '제목')}>
                    <input
                      type="text"
                      maxLength={200}
                      value={form.subject}
                      onChange={(e) => set('subject', e.target.value)}
                      className={denseInputCls}
                      style={denseInputStyle}
                      aria-label={t('template.field.subject', '제목')}
                    />
                  </FormSection>
                  {/* ID-226 — EMAIL HTML 본문 발송 토글. ON 이면 본문을 HTML 소스로 작성·HTML 발송,
                      OFF 면 기존 plain-text(회귀0). 미리보기 패널이 토글에 맞춰 렌더 방식 전환. */}
                  <FormSection
                    density="compact"
                    label={t('template.field.isHtml', 'HTML 본문')}
                    hint={t(
                      'template.hint.isHtml',
                      'HTML 소스로 작성해 디자인된 이메일을 발송합니다. 끄면 일반 텍스트로 발송됩니다.',
                    )}
                  >
                    <Switch
                      checked={form.isHtml}
                      onCheckedChange={(v) => set('isHtml', v)}
                      aria-label={t('template.field.isHtml', 'HTML 본문')}
                    />
                  </FormSection>
                </>
              ) : null}

              <FormSection density="compact" required label={t('template.field.body', '본문')}>
                <textarea
                  ref={bodyRef}
                  rows={isEmail && form.isHtml ? 16 : 10}
                  maxLength={isEmail && form.isHtml ? 100_000 : 10_000}
                  value={form.body}
                  onChange={(e) => set('body', e.target.value)}
                  spellCheck={!(isEmail && form.isHtml)}
                  placeholder={
                    isEmail && form.isHtml
                      ? t(
                          'template.hint.bodyHtmlPlaceholder',
                          'HTML 소스를 입력하세요. {rc_변수}·#{변수} 형식으로 변수를 삽입할 수 있습니다.',
                        )
                      : t(
                          'template.hint.bodyKakaoPlaceholder',
                          '알림톡 본문을 입력하세요. #{변수} 형식으로 변수를 삽입할 수 있습니다.',
                        )
                  }
                  className={`w-full rounded-md px-2.5 py-2 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]${
                    isEmail && form.isHtml ? ' font-mono' : ''
                  }`}
                  style={{ ...denseInputStyle, resize: 'vertical' }}
                  aria-label={t('template.field.body', '본문')}
                />
              </FormSection>

              <FormSection density="compact" label={t('template.field.variables', '변수')}>
                <div className="flex flex-col gap-3">
                  {/* 본문의 #{변수} 토큰을 자동 추출 — 운영자는 예시 값만 입력(미리보기·테스트 발송용). */}
                  <div className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-medium" style={{ color: 'var(--content-muted)' }}>
                      {t('template.variables.usedTitle', '예시 값')}
                    </span>
                    {variables.length === 0 ? (
                      <span className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                        {t('template.variables.noneUsed', '본문에 아직 변수가 없습니다.')}
                      </span>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {variables.map((v, idx) => (
                          <div key={v.key} className="flex items-center gap-2">
                            <span
                              className="inline-flex h-8 w-[40%] min-w-[120px] items-center rounded-md px-2 font-mono text-[12px]"
                              style={{ background: 'var(--surface-1)', color: 'var(--content-default)' }}
                            >
                              {tokenDisplay(v.key)}
                            </span>
                            <input
                              type="text"
                              value={v.example}
                              onChange={(e) =>
                                setVariables((prev) =>
                                  prev.map((x, i) => (i === idx ? { ...x, example: e.target.value } : x)),
                                )
                              }
                              placeholder={t('template.variables.exampleValuePlaceholder', '예시 값')}
                              className="h-8 flex-1 rounded-md px-2.5 text-[13px] outline-none focus-visible:[box-shadow:var(--focus-ring-brand)]"
                              style={denseInputStyle}
                              aria-label={`${v.key} ${t('template.variables.exampleValuePlaceholder', '예시 값')}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    <span className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                      {t('template.variables.usedHint', '본문에 사용한 변수의 예시 값을 입력하세요. 미리보기·테스트 발송에 사용됩니다.')}
                    </span>
                  </div>
                </div>
              </FormSection>
            </div>
          </SectionCard>
        ) : null}

        {/* 단계 3 — 디자인 설정(알림톡 중심) */}
        {step === 2 ? (
          <div className="flex flex-col gap-4 lg:flex-row">
            <SectionCard
              className="flex-1"
              title={t('template.section.design', '디자인 설정')}
              description={t('template.step.design', '디자인 설정')}
            >
              <div className="flex flex-col gap-5">
                {isKakao ? (
                  <>
                    <FormSection density="compact" required label={t('template.field.kakaoType', '알림톡 유형')}>
                      <SegmentedControl<KakaoTemplateType>
                        value={form.kakaoTemplateType}
                        onChange={(v) => set('kakaoTemplateType', v)}
                        options={[
                          { value: 'basic', label: t('template.kakaoType.basic', '기본형') },
                          { value: 'emphasis', label: t('template.kakaoType.emphasis', '강조표기형') },
                          { value: 'image', label: t('template.kakaoType.image', '이미지형') },
                        ]}
                        aria-label={t('template.field.kakaoType', '알림톡 유형')}
                      />
                    </FormSection>

                    {form.kakaoTemplateType === 'emphasis' ? (
                      <>
                        <FormSection density="compact" required label={t('template.field.emphasisTitle', '강조 제목')}>
                          <input
                            type="text"
                            maxLength={EMPHASIS_TITLE_MAX_LENGTH}
                            value={form.emphasisTitle}
                            onChange={(e) => set('emphasisTitle', e.target.value)}
                            className={denseInputCls}
                            style={denseInputStyle}
                            aria-label={t('template.field.emphasisTitle', '강조 제목')}
                          />
                        </FormSection>
                        <FormSection density="compact" required label={t('template.field.emphasisSubtitle', '강조 부제')}>
                          <input
                            type="text"
                            maxLength={EMPHASIS_SUBTITLE_MAX_LENGTH}
                            value={form.emphasisSubtitle}
                            onChange={(e) => set('emphasisSubtitle', e.target.value)}
                            className={denseInputCls}
                            style={denseInputStyle}
                            aria-label={t('template.field.emphasisSubtitle', '강조 부제')}
                          />
                        </FormSection>
                      </>
                    ) : null}

                    {form.kakaoTemplateType === 'image' ? (
                      <FormSection density="compact" label={t('template.field.imageUrl', '이미지 URL')}>
                        <input
                          type="url"
                          value={form.imageUrl}
                          onChange={(e) => set('imageUrl', e.target.value)}
                          placeholder="https://"
                          className={denseInputCls}
                          style={denseInputStyle}
                          aria-label={t('template.field.imageUrl', '이미지 URL')}
                        />
                      </FormSection>
                    ) : null}

                    <FormSection density="compact" label={t('template.field.reprLinkMobile', '대표링크(모바일)')}>
                      <input
                        type="url"
                        value={form.reprLinkMobile}
                        onChange={(e) => set('reprLinkMobile', e.target.value)}
                        placeholder="https://"
                        className={denseInputCls}
                        style={denseInputStyle}
                        aria-label={t('template.field.reprLinkMobile', '대표링크(모바일)')}
                      />
                    </FormSection>
                    <FormSection density="compact" label={t('template.field.reprLinkPc', '대표링크(PC)')}>
                      <input
                        type="url"
                        value={form.reprLinkPc}
                        onChange={(e) => set('reprLinkPc', e.target.value)}
                        placeholder="https://"
                        className={denseInputCls}
                        style={denseInputStyle}
                        aria-label={t('template.field.reprLinkPc', '대표링크(PC)')}
                      />
                    </FormSection>

                    {/* 버튼 목록 */}
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] font-medium" style={{ color: 'var(--content-strong)' }}>
                          {t('template.section.buttons', '버튼')}
                        </span>
                        <Button variant="outline" size="sm" onClick={addButton}>
                          <Plus size={14} aria-hidden="true" />
                          {t('template.buttons.add', '버튼 추가')}
                        </Button>
                      </div>
                      {buttons.length === 0 ? (
                        <EmptyState size="inline" title={t('template.buttons.empty', '등록된 버튼이 없습니다.')} />
                      ) : (
                        buttons.map((b, idx) => (
                          <div
                            key={idx}
                            className="flex flex-col gap-2 rounded-md p-3"
                            style={{ border: '1px solid var(--divider-strong)' }}
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-[12px] font-medium" style={{ color: 'var(--content-strong)' }}>
                                {idx + 1}
                              </span>
                              <Button variant="ghost" size="sm" onClick={() => removeButton(idx)}>
                                <Trash2 size={13} aria-hidden="true" style={{ color: 'var(--signal-danger)' }} />
                                {t('template.buttons.remove', '버튼 삭제')}
                              </Button>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <div className="min-w-[140px]">
                                <RadixSelect
                                  value={b.type}
                                  onChange={(v) => updateButton(idx, { type: v as KakaoButtonType })}
                                  options={buttonTypeOptions}
                                  aria-label={t('template.buttons.type', '버튼 유형')}
                                />
                              </div>
                              <input
                                type="text"
                                value={b.name}
                                onChange={(e) => updateButton(idx, { name: e.target.value })}
                                placeholder={t('template.buttons.name', '버튼명')}
                                className={`${denseInputCls} flex-1`}
                                style={denseInputStyle}
                                aria-label={t('template.buttons.name', '버튼명')}
                              />
                            </div>
                            {b.type === 'WL' || b.type === 'AL' ? (
                              <div className="flex flex-wrap gap-2">
                                <input
                                  type="url"
                                  value={b.urlMobile ?? ''}
                                  onChange={(e) => updateButton(idx, { urlMobile: e.target.value })}
                                  placeholder={t('template.buttons.urlMobile', '모바일 URL')}
                                  className={`${denseInputCls} flex-1`}
                                  style={denseInputStyle}
                                  aria-label={t('template.buttons.urlMobile', '모바일 URL')}
                                />
                                <input
                                  type="url"
                                  value={b.urlPc ?? ''}
                                  onChange={(e) => updateButton(idx, { urlPc: e.target.value })}
                                  placeholder={t('template.buttons.urlPc', 'PC URL')}
                                  className={`${denseInputCls} flex-1`}
                                  style={denseInputStyle}
                                  aria-label={t('template.buttons.urlPc', 'PC URL')}
                                />
                              </div>
                            ) : (
                              <p className="text-[11px]" style={{ color: 'var(--content-muted)' }}>
                                {t('template.buttons.noUrlHint', '이 버튼 유형은 추가 입력이 없습니다.')}
                              </p>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
                    {t('template.preview.emptyBody', '본문을 입력하면 미리보기가 표시됩니다.')}
                  </p>
                )}
              </div>
            </SectionCard>

            {/* 우측 실시간 미리보기 */}
            <div className={previewColCls}>
              <SectionCard title={t('template.preview.title', '미리보기')}>
                {previewPanel}
              </SectionCard>
            </div>
          </div>
        ) : null}

        {/* 단계 4 — 발송 설정 + 미리보기 */}
        {step === 3 ? (
          <div className="flex flex-col gap-4 lg:flex-row">
            <SectionCard
              className="flex-1"
              title={t('template.section.preview', '미리보기')}
              description={t('template.step.preview', '미리보기')}
            >
              <div className="flex flex-col gap-5">
                <FormSection
                  density="compact"
                  label={t('template.field.autoSendEnabled', '자동 발송')}
                  hint={t('template.hint.autoSendEnabled', '자동 알림 규칙에서 이 템플릿을 사용할 수 있도록 합니다.')}
                >
                  <Switch
                    checked={form.autoSendEnabled}
                    onCheckedChange={(v) => set('autoSendEnabled', v)}
                    aria-label={t('template.field.autoSendEnabled', '자동 발송')}
                  />
                </FormSection>
                <FormSection
                  density="compact"
                  label={t('template.field.isAdvertising', '광고성')}
                  hint={t('template.hint.isAdvertising', '광고성 정보 표시 의무 — 정보통신망법 §50')}
                >
                  <Switch
                    checked={form.isAdvertising}
                    onCheckedChange={(v) => set('isAdvertising', v)}
                    aria-label={t('template.field.isAdvertising', '광고성')}
                  />
                </FormSection>
                {errors.length > 0 ? (
                  <ul
                    className="flex flex-col gap-1 rounded-md p-3 text-[12px]"
                    style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
                  >
                    {errors.map((e, i) => (
                      <li key={i}>· {e}</li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </SectionCard>
            <div className={previewColCls}>
              <SectionCard title={t('template.preview.title', '미리보기')}>
                {previewPanel}
              </SectionCard>
            </div>
          </div>
        ) : null}

        <StickyFooter>
          {isEdit && canWrite ? (
            <Button
              type="button"
              variant="danger"
              size="sm"
              onClick={() => setConfirmDelete(true)}
              disabled={isBusy}
            >
              {t('template.actions.delete', '삭제')}
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/notifications?tab=template')}
            disabled={isBusy}
          >
            {t('common.cancel', '취소')}
          </Button>
          {step > 0 ? (
            <Button type="button" variant="outline" size="sm" onClick={() => setStep((s) => s - 1)} disabled={isBusy}>
              {t('common.prev', '이전')}
            </Button>
          ) : null}
          {step < steps.length - 1 ? (
            <Button type="button" size="sm" onClick={() => setStep((s) => s + 1)} disabled={isBusy}>
              {t('common.next', '다음')}
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={onSubmit} disabled={isBusy || !canWrite}>
              {isBusy
                ? t('common.saving', '저장 중…')
                : isEdit
                  ? t('template.actions.save', '저장')
                  : t('template.actions.create', '템플릿 등록')}
            </Button>
          )}
        </StickyFooter>
      </div>

      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('template.deleteDialog.title', '템플릿 삭제')}</DialogTitle>
            <DialogDescription>
              {t(
                'template.deleteDialog.description',
                '정말 삭제하시겠습니까? 발송 이력은 유지되지만 이후 발송에는 사용할 수 없습니다.',
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" size="sm">
                {t('common.cancel', '취소')}
              </Button>
            </DialogClose>
            <Button variant="danger" size="sm" disabled={deleteMutation.isPending} onClick={handleDelete}>
              {deleteMutation.isPending ? t('common.deleting', '삭제 중…') : t('common.delete', '삭제')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
};
